import type { Ref } from 'vue'

import {
  GroupReorderDocument,
  GroupSortByRoleDocument,
  PlayerMoveDocument,
  RoomMoveDocument,
} from '@/shared/api/gql/graphql'
import type { GroupId, Player, PlayerId, PlayerGroup, Room, RoomId } from '../../types'

export interface OptimisticMovesCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly players: Ref<ReadonlyArray<Player>>
  readonly groups: Ref<ReadonlyArray<PlayerGroup>>
  readonly rooms: Ref<ReadonlyArray<Room>>
  readonly runMutate: <_T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    options?: {
      readonly onError?: () => void
    },
  ) => Promise<boolean>
  readonly run: <T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    done?: (result: T) => void,
  ) => Promise<void>
  readonly gid: (id: GroupId) => number
  readonly rid: (id: RoomId) => number
}

export interface OptimisticMovesApi {
  readonly applyMoveLocally: (
    playerId: PlayerId,
    targetGroupId: GroupId,
    insertIndex: number,
  ) => (() => void) | null
  readonly applyReorderLocally: (
    groupId: GroupId,
    orderedIds: ReadonlyArray<PlayerId>,
  ) => (() => void) | null
  readonly applyRoomMoveLocally: (
    roomId: RoomId,
    targetIndex: number,
  ) => (() => void) | null
  readonly movePlayerToGroup: (
    playerId: string,
    targetGroupId: GroupId,
    insertIndex: number,
  ) => {
    readonly rollback: () => void
    readonly result: Promise<boolean>
  }
  readonly reorderGroup: (
    groupId: GroupId,
    orderedIds: ReadonlyArray<string>,
  ) => { readonly rollback: () => void }
  readonly sortGroupByRole: (groupId: GroupId) => void
  readonly sortAllGroups: () => void
  readonly moveRoom: (roomId: RoomId, targetIndex: number) => { readonly rollback: () => void }
}

export function useOptimisticMoves(ctx: OptimisticMovesCtx): OptimisticMovesApi {
  function applyMoveLocally(
    playerId: PlayerId,
    targetGroupId: GroupId,
    insertIndex: number,
  ): (() => void) | null {
    const playerIdx = ctx.players.value.findIndex((p) => p.id === playerId)
    if (playerIdx === -1) return null
    const playerBefore = ctx.players.value[playerIdx]
    if (playerBefore === undefined) return null
    const sourceGroupId = playerBefore.group

    const targetGroup = ctx.groups.value.find((g) => g.id === targetGroupId)
    if (targetGroup === undefined) return null

    if (sourceGroupId === targetGroupId) {
      const currentIndex = targetGroup.playerIds.indexOf(playerId)
      if (currentIndex === insertIndex) return null
      const safeIndex = Math.max(0, Math.min(insertIndex, targetGroup.playerIds.length))
      const snapshot = targetGroup.playerIds.slice()
      const nextOrder = targetGroup.playerIds.slice()
      nextOrder.splice(currentIndex, 1)
      nextOrder.splice(safeIndex, 0, playerId)
      ctx.groups.value = ctx.groups.value.map((g) =>
        g.id === targetGroupId ? { ...g, playerIds: nextOrder } : g,
      )
      return () => {
        ctx.groups.value = ctx.groups.value.map((g) =>
          g.id === targetGroupId ? { ...g, playerIds: snapshot } : g,
        )
      }
    }

    const safeIndex = Math.max(0, Math.min(insertIndex, targetGroup.playerIds.length))
    const sourceSnapshot = sourceGroupId !== null
      ? ctx.groups.value.find((g) => g.id === sourceGroupId)?.playerIds.slice() ?? null
      : null
    const targetSnapshot = targetGroup.playerIds.slice()
    const playerSnapshot: Player = playerBefore

    ctx.groups.value = ctx.groups.value.map((g) => {
      if (g.id === targetGroupId) {
        const next = g.playerIds.slice()
        next.splice(safeIndex, 0, playerId)
        return { ...g, playerIds: next }
      }
      if (sourceGroupId !== null && g.id === sourceGroupId) {
        return { ...g, playerIds: g.playerIds.filter((id) => id !== playerId) }
      }
      return g
    })
    ctx.players.value = ctx.players.value.map((p) =>
      p.id === playerId ? { ...p, group: targetGroupId } : p,
    )

    return () => {
      ctx.players.value = ctx.players.value.map((p) =>
        p.id === playerId ? playerSnapshot : p,
      )
      ctx.groups.value = ctx.groups.value.map((g) => {
        if (g.id === targetGroupId) return { ...g, playerIds: targetSnapshot }
        if (sourceGroupId !== null && g.id === sourceGroupId && sourceSnapshot !== null) {
          return { ...g, playerIds: sourceSnapshot }
        }
        return g
      })
    }
  }

  function applyReorderLocally(
    groupId: GroupId,
    orderedIds: ReadonlyArray<PlayerId>,
  ): (() => void) | null {
    const group = ctx.groups.value.find((g) => g.id === groupId)
    if (group === undefined) return null
    const current = group.playerIds
    if (current.length === orderedIds.length) {
      let sameOrder = true
      for (let i = 0; i < current.length; i += 1) {
        if (current[i] !== orderedIds[i]) {
          sameOrder = false
          break
        }
      }
      if (sameOrder) return null
    }
    const snapshot = current.slice()
    ctx.groups.value = ctx.groups.value.map((g) =>
      g.id === groupId ? { ...g, playerIds: orderedIds.slice() } : g,
    )
    return () => {
      ctx.groups.value = ctx.groups.value.map((g) =>
        g.id === groupId ? { ...g, playerIds: snapshot } : g,
      )
    }
  }

  function applyRoomMoveLocally(
    roomId: RoomId,
    targetIndex: number,
  ): (() => void) | null {
    const currentIndex = ctx.rooms.value.findIndex((r) => r.id === roomId)
    if (currentIndex === -1) return null
    if (ctx.rooms.value.length === 0) return null
    const safeIndex = Math.max(0, Math.min(targetIndex, ctx.rooms.value.length - 1))
    if (currentIndex === safeIndex) return null
    const snapshot = ctx.rooms.value.slice()
    const moved = ctx.rooms.value[currentIndex]
    if (moved === undefined) return null
    const next = ctx.rooms.value.slice()
    next.splice(currentIndex, 1)
    next.splice(safeIndex, 0, moved)
    ctx.rooms.value = next
    return () => {
      ctx.rooms.value = snapshot
    }
  }

  function movePlayerToGroup(
    playerId: string,
    targetGroupId: GroupId,
    insertIndex: number,
  ): {
    readonly rollback: () => void
    readonly result: Promise<boolean>
  } {
    const rollback = applyMoveLocally(playerId, targetGroupId, insertIndex) ?? ((): void => {})
    const result = ctx.runMutate(
      PlayerMoveDocument,
      {
        guildId: ctx.activeDiscordGuildId.value,
        playerId: Number(playerId),
        targetGroupId: ctx.gid(targetGroupId),
        insertIndex,
      },
      {
        onError: () => rollback(),
      },
    )
    return {
      rollback: () => rollback(),
      result,
    }
  }

  function reorderGroup(
    groupId: GroupId,
    orderedIds: ReadonlyArray<string>,
  ): { readonly rollback: () => void } {
    const rollback = applyReorderLocally(groupId, orderedIds) ?? ((): void => {})
    void ctx.runMutate(
      GroupReorderDocument,
      {
        guildId: ctx.activeDiscordGuildId.value,
        groupId: ctx.gid(groupId),
        orderedPlayerIds: orderedIds.map(Number),
      },
      {
        onError: () => rollback(),
      },
    )
    return { rollback: () => rollback() }
  }

  function sortGroupByRole(groupId: GroupId): void {
    void ctx.run(GroupSortByRoleDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      groupId: ctx.gid(groupId),
    })
  }

  function sortAllGroups(): void {
    for (const g of ctx.groups.value) sortGroupByRole(g.id)
  }

  function moveRoom(
    roomId: RoomId,
    targetIndex: number,
  ): { readonly rollback: () => void } {
    const rollback = applyRoomMoveLocally(roomId, targetIndex) ?? ((): void => {})
    void ctx.runMutate(
      RoomMoveDocument,
      {
        guildId: ctx.activeDiscordGuildId.value,
        roomId: ctx.rid(roomId),
        targetIndex,
      },
      {
        onError: () => rollback(),
      },
    )
    return { rollback: () => rollback() }
  }

  return {
    applyMoveLocally,
    applyReorderLocally,
    applyRoomMoveLocally,
    movePlayerToGroup,
    reorderGroup,
    sortGroupByRole,
    sortAllGroups,
    moveRoom,
  }
}
